import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAuditLogs,
  getSystemHealth,
  getSystemStats,
  getUserManagement,
} from '@/lib/api';
import { v2Api } from '@/lib/api/v2';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminNotificationsWebSocket } from '@/hooks/useAdminNotificationsWebSocket';
import type { Notification } from '@/types/dashboard';
import {
  ShieldCheck,
  UsersRound,
  ListChecks,
  Layers,
  Timer,
  Activity,
  ClipboardList,
  Bell,
} from 'lucide-react';
import { formatDate, formatNumber } from '@/lib/utils';
import { AdminActionsPanel } from '@/components/admin/AdminActionsPanel';

export default function AdminPage() {
  const { user } = useAuth();
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  const [stats, setStats] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [adminNotifications, setAdminNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      try {
        const [statsData, healthData, usersData, logsData] = await Promise.all([
          getSystemStats(),
          getSystemHealth(),
          getUserManagement({ limit: 8 }),
          getAuditLogs({ limit: 10 }),
        ]);
        setStats(statsData);
        setHealth(healthData);
        setUsers(usersData || []);
        setAuditLogs(logsData || []);

        if (isStaff) {
          const page = await v2Api.dashboard.notifications.list({ page: 1, page_size: 50 });
          setAdminNotifications(page.results);
        } else {
          setAdminNotifications([]);
        }
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [isStaff],
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const refreshSilently = useCallback(() => {
    void loadData({ silent: true });
  }, [loadData]);

  useAdminNotificationsWebSocket(isStaff, refreshSilently);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-neutral-500">Loading...</div>
      </div>
    );
  }

  const notifUnread = adminNotifications.filter((n) => !n.read).length;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-neutral-900">
          <ShieldCheck className="h-6 w-6 text-neutral-500" aria-hidden />
          Admin Console
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          Observe mission-critical metrics, moderate users, and execute maintenance workflows for the cyber
          range quiz infrastructure.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Active users (today)
            </p>
            <UsersRound className="h-5 w-5 text-neutral-400" aria-hidden />
          </div>
          <p className="mt-3 text-3xl font-semibold text-neutral-900">
            {formatNumber(stats?.active_users_today)}
          </p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Total questions
            </p>
            <ListChecks className="h-5 w-5 text-neutral-400" aria-hidden />
          </div>
          <p className="mt-3 text-3xl font-semibold text-neutral-900">
            {formatNumber(stats?.total_questions)}
          </p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Total quizzes</p>
            <Layers className="h-5 w-5 text-neutral-400" aria-hidden />
          </div>
          <p className="mt-3 text-3xl font-semibold text-neutral-900">
            {formatNumber(stats?.total_quizzes)}
          </p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">System uptime</p>
            <Timer className="h-5 w-5 text-neutral-400" aria-hidden />
          </div>
          <p className="mt-3 text-3xl font-semibold text-neutral-900">{stats?.system_uptime ?? '—'}</p>
        </div>
      </section>

      {isStaff && (
        <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
                <Bell className="h-5 w-5 text-neutral-500" aria-hidden />
                All notifications
              </h3>
              <p className="mt-1 text-sm text-neutral-500">
                System-wide inbox (all users). Updates live when new rows are stored.{' '}
                <Link to="/notifications" className="text-primary underline-offset-4 hover:underline">
                  Open personal notifications
                </Link>
              </p>
            </div>
            <p className="text-sm text-neutral-600">
              <span className="font-semibold text-neutral-800">{adminNotifications.length}</span> shown
              {notifUnread > 0 ? (
                <span className="text-primary">
                  {' '}
                  · {notifUnread} unread
                </span>
              ) : null}
            </p>
          </div>

          <div className="mt-5 overflow-hidden rounded-lg border border-neutral-100">
            <table className="min-w-full divide-y divide-neutral-100 text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-neutral-600">User ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-neutral-600">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-neutral-600">Title</th>
                  <th className="px-4 py-3 text-left font-semibold text-neutral-600">Time</th>
                  <th className="px-4 py-3 text-left font-semibold text-neutral-600">Read</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {!adminNotifications.length ? (
                  <tr>
                    <td className="px-4 py-5 text-center text-neutral-500" colSpan={5}>
                      No notifications in the database yet.
                    </td>
                  </tr>
                ) : (
                  adminNotifications.map((n) => (
                    <tr key={n.id ?? `${n.user_id}-${n.timestamp}`}>
                      <td className="px-4 py-3 font-mono text-xs text-neutral-700">{n.user_id ?? '—'}</td>
                      <td className="px-4 py-3 text-neutral-700">{n.type}</td>
                      <td className="max-w-md truncate px-4 py-3 text-neutral-800" title={n.title}>
                        {n.title}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-neutral-600">
                        {formatDate(n.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                            n.read ? 'bg-neutral-200 text-neutral-600' : 'bg-sky-100 text-sky-800'
                          }`}
                        >
                          {n.read ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
          <Activity className="h-5 w-5 text-neutral-500" aria-hidden />
          Platform health
        </h3>
        <p className="text-sm text-neutral-500">Operational telemetry sourced from `/api/admin/health`.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {health ? (
            Object.entries(health).map(([key, value]) => (
              <div
                key={key}
                className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600"
              >
                <p className="font-semibold text-neutral-800">{key.replace(/_/g, ' ')}</p>
                <p className="mt-1 text-neutral-700">
                  {typeof value === 'number'
                    ? formatNumber(value)
                    : typeof value === 'string'
                      ? value
                      : JSON.stringify(value)}
                </p>
              </div>
            ))
          ) : (
            <p className="rounded-md border border-dashed border-neutral-200 p-4 text-sm text-neutral-500">
              Health metrics unavailable.
            </p>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
          <UsersRound className="h-5 w-5 text-neutral-500" aria-hidden />
          User management snapshot
        </h3>
        <p className="text-sm text-neutral-500">Most recent users from `/api/admin/users`.</p>
        <div className="mt-5 overflow-hidden rounded-lg border border-neutral-100">
          <table className="min-w-full divide-y divide-neutral-100 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">Email</th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">Username</th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">Role</th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">Last login</th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {!users.length ? (
                <tr>
                  <td className="px-4 py-5 text-center text-neutral-500" colSpan={5}>
                    No users provisioned yet.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.user_id}>
                    <td className="px-4 py-3 text-neutral-700">{u.email}</td>
                    <td className="px-4 py-3 text-neutral-700">{u.username}</td>
                    <td className="px-4 py-3 text-neutral-700">{u.role ?? 'user'}</td>
                    <td className="px-4 py-3 text-neutral-700">{formatDate(u.last_login)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                          u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'
                        }`}
                      >
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
          <ClipboardList className="h-5 w-5 text-neutral-500" aria-hidden />
          Audit trail
        </h3>
        <p className="text-sm text-neutral-500">High-volume actions recorded by `/api/admin/audit-logs`.</p>
        <div className="mt-4 space-y-3">
          {!auditLogs.length ? (
            <p className="rounded-md border border-dashed border-neutral-200 p-4 text-sm text-neutral-500">
              No audit events logged yet.
            </p>
          ) : (
            auditLogs.map((entry) => (
              <article
                key={`${entry.timestamp}-${entry.action}-${entry.entity_id}`}
                className="rounded-md border border-neutral-100 bg-neutral-50 p-4 text-sm text-neutral-600"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-500">
                  <span>{formatDate(entry.timestamp)}</span>
                  <span>User: {entry.user_id ?? 'system'}</span>
                  <span>IP: {entry.ip_address ?? '—'}</span>
                </div>
                <p className="mt-2 text-neutral-800">
                  <span className="font-semibold text-neutral-900">{entry.action}</span> on {entry.entity_type}{' '}
                  ({entry.entity_id || 'N/A'})
                </p>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-neutral-900/5 p-3 text-[11px]">
                  {JSON.stringify(entry.details, null, 2)}
                </pre>
              </article>
            ))
          )}
        </div>
      </section>

      <AdminActionsPanel />
    </div>
  );
}
